/**
 * `/web/auth/callback` — the OAuth redirect target (web/docs/infra.md
 * §Routing). Exchanges the authorization code via `completeSocialSignIn`,
 * stores the session through the auth provider, then lands on the page the
 * user came from. Public route — outside the app shell and the guard.
 */

import { useEffect, useState } from 'react';
import { Loader2 } from 'lucide-react';
import { Link, useNavigate } from 'react-router-dom';

import { completeSocialSignIn } from '@/api/auth/socialAuth';
import { useAuth } from '@/auth/authContext';
import { Button } from '@/components/ui/button';
import { useT } from '@/i18n/locale';

import { AuthLayout } from '@/features/auth/AuthLayout';

export function AuthCallbackRoute() {
  const navigate = useNavigate();
  const { signIn } = useAuth();
  const [failed, setFailed] = useState(false);
  const t = useT();

  useEffect(() => {
    let active = true;
    (async () => {
      try {
        const result = await completeSocialSignIn(new URL(window.location.href));
        if (!active) return;
        if (!result) {
          // No pending flow / cancelled / invalid response.
          navigate('/login', { replace: true });
          return;
        }
        await signIn(result.session);
        navigate(result.returnTo, { replace: true });
      } catch {
        if (active) setFailed(true);
      }
    })();
    return () => {
      active = false;
    };
  }, [navigate, signIn]);

  if (failed) {
    return (
      <AuthLayout title={t('auth.callback.failedTitle')} lede={t('auth.callback.failedBody')}>
        <Button asChild variant="neutral" size="lg" className="w-full">
          <Link to="/login">{t('auth.callback.back')}</Link>
        </Button>
      </AuthLayout>
    );
  }

  return (
    <AuthLayout title={t('auth.callback.signingIn')}>
      <div className="flex justify-center py-10" role="status">
        <Loader2 className="h-6 w-6 animate-spin text-ink-secondary" aria-hidden="true" />
        <span className="sr-only">{t('auth.callback.signingIn')}</span>
      </div>
    </AuthLayout>
  );
}
